import { Type } from '@angular/core';
import { dataGet } from '@kinet-open-network/shared/util';
import { plainToInstance } from 'class-transformer';
import { Observable, map } from 'rxjs';
import { BaseModel, BaseModelWoId } from './base.model';

type FlattenKeys<T, Prefix extends string = ''> = T extends object
  ? {
      [K in keyof T & string]:
        | `${Prefix}${K}`
        | FlattenKeys<T[K], `${Prefix}${K}.`>;
    }[keyof T & string]
  : Prefix;

export const toModel = <T extends object, R extends BaseModel>(
  data: T,
  model: Type<R>,
  path?: FlattenKeys<T>,
) => {
  if (path) {
    return plainToInstance(model, dataGet(data, path)) as R;
  }

  return plainToInstance(model, data) as R;
};

export const toModelArray = <T extends object, R extends BaseModel>(
  data: T[],
  model: Type<R>,
  path?: FlattenKeys<T>,
) =>
  data.map((item) => {
    if (path) {
      return plainToInstance(model, dataGet(item, path));
    }

    return plainToInstance(model, item);
  });

export const mapToModel =
  <T extends object, R extends BaseModel | BaseModelWoId>(
    model: Type<R>,
    path?: FlattenKeys<T>,
  ) =>
  (source: Observable<T>) => {
    return source.pipe(
      map((data) => {
        if (path) {
          return plainToInstance(model, dataGet(data, path)) as R;
        }

        return plainToInstance(model, data) as R;
      }),
    );
  };

export const mapToModelArray = <
  T extends object,
  R extends BaseModel | BaseModelWoId,
>(
  model: Type<R>,
  path?: FlattenKeys<T>,
) => {
  return map<T[], R[]>((data: T[]) => {
    if (path) {
      return dataGet(data, path).map((item: unknown) =>
        plainToInstance(model, item),
      );
    }

    return data.map((item) => plainToInstance(model, item));
  });
};
